import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { CheckCircle, Trash, XCircle } from "@phosphor-icons/react";

const EMPTY = { name: "", type: "Customer", gstin: "", pan: "", phone: "", email: "", address: "", credit_limit: 0, opening_balance: 0 };

export default function Parties() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [gstInfo, setGstInfo] = useState(null);
  const [outstanding, setOutstanding] = useState({});

  const load = async () => {
    const [p, r, pay] = await Promise.all([
      api.get("/parties"),
      api.get("/reports/outstanding?kind=receivable"),
      api.get("/reports/outstanding?kind=payable"),
    ]);
    setRows(p.data);
    const map = {};
    [...r.data.rows, ...pay.data.rows].forEach((o) => { if (o.party_id) map[o.party_id] = o; });
    setOutstanding(map);
  };
  useEffect(() => { load(); }, []);

  const verify = async () => {
    try {
      const { data } = await api.get(`/companies/gstin/${form.gstin.toUpperCase()}`);
      setGstInfo(data);
      setForm((f) => ({ ...f, pan: data.pan || f.pan }));
    } catch (e) { setGstInfo({ valid: false }); toast.error(apiError(e)); }
  };

  const create = async () => {
    try {
      await api.post("/parties", {
        ...form, gstin: form.gstin.toUpperCase() || null,
        credit_limit: Number(form.credit_limit), opening_balance: Number(form.opening_balance),
      });
      toast.success("Party created with linked ledger");
      setOpen(false); setForm(EMPTY); setGstInfo(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/parties/${id}`); toast.success("Party deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = rows.filter((r) =>
    (tab === "all" || r.type === tab) && (!q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.gstin || "").includes(q.toUpperCase())));

  return (
    <div className="space-y-6" data-testid="parties-page">
      <PageHeader title="Parties" subtitle="Customers & vendors — each gets an auto-linked sub-ledger under Sundry Debtors/Creditors.">
        <Input placeholder="Search name or GSTIN…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-8 w-56 text-sm" data-testid="party-search-input" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="new-party-btn">New party</Button></DialogTrigger>
          <DialogContent className="max-w-xl" data-testid="party-dialog">
            <DialogHeader><DialogTitle className="font-display">Create party</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest">Name</Label>
                <Input data-testid="party-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="party-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Customer">Customer</SelectItem>
                    <SelectItem value="Vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">GSTIN</Label>
                <div className="flex gap-2">
                  <Input className="num uppercase" data-testid="party-gstin-input" value={form.gstin}
                    onChange={(e) => { setForm({ ...form, gstin: e.target.value.toUpperCase() }); setGstInfo(null); }} />
                  <Button type="button" variant="outline" size="sm" onClick={verify} data-testid="party-gstin-verify">Verify</Button>
                </div>
                {gstInfo ? (
                  <p className={`flex items-center gap-1 text-xs ${gstInfo.valid ? "text-success" : "text-destructive"}`} data-testid="party-gstin-status">
                    {gstInfo.valid ? <CheckCircle size={13} /> : <XCircle size={13} />}
                    {gstInfo.valid ? `${gstInfo.state} · Active` : "Invalid GSTIN"}
                  </p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Phone</Label>
                <Input className="num" data-testid="party-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <Input data-testid="party-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Credit limit</Label>
                <Input type="number" className="num" data-testid="party-credit-input" value={form.credit_limit}
                  onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Opening balance</Label>
                <Input type="number" className="num" data-testid="party-opening-input" value={form.opening_balance}
                  onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest">Address</Label>
                <Input data-testid="party-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.name} data-testid="save-party-btn">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="party-tabs">
          <TabsTrigger value="all" data-testid="party-tab-all">All ({rows.length})</TabsTrigger>
          <TabsTrigger value="Customer" data-testid="party-tab-customer">Customers ({rows.filter((r) => r.type === "Customer").length})</TabsTrigger>
          <TabsTrigger value="Vendor" data-testid="party-tab-vendor">Vendors ({rows.filter((r) => r.type === "Vendor").length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <Empty label="No parties yet" hint="Add your first customer to raise an invoice." />
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="dense-table w-full min-w-[860px]" data-testid="parties-table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>GSTIN</th><th>State</th><th>Contact</th>
                <th className="text-right">Outstanding</th><th className="text-right">Ageing</th><th /></tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const o = outstanding[p.id];
                return (
                  <tr key={p.id} className="row-in" data-testid={`party-row-${p.id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td className="text-muted-foreground">{p.type}</td>
                    <td className="num">
                      {p.gstin || "—"}
                      {p.gstin ? (
                        <span className={`ml-2 text-[10px] uppercase ${p.gstin_valid ? "text-success" : "text-destructive"}`}>
                          {p.gstin_valid ? "valid" : "check"}
                        </span>
                      ) : null}
                    </td>
                    <td className="text-muted-foreground">{p.state || "—"}</td>
                    <td className="num text-muted-foreground">{p.phone || p.email || "—"}</td>
                    <td className="num text-right font-medium">{o ? money(o.amount) : "—"}</td>
                    <td className="num text-right text-muted-foreground">{o ? `${o.bucket} d` : "—"}</td>
                    <td className="text-right">
                      <button onClick={() => remove(p.id)} data-testid={`delete-party-${p.id}`}
                        className="text-muted-foreground hover:text-destructive">
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
